<template>
  <div>
    <client-secret-modal
      :visible.sync="showSecret"
      :client-id="issued.client_id"
      :client-secret="issued.client_secret"
    />

    <b-overlay :show="loading" rounded="sm">
      <!-- Identity summary -->
      <b-card no-body class="border mb-2">
        <b-card-body>
          <b-row>
            <b-col cols="12" md="8">
              <h4 class="mb-25">{{ form.title || 'API client' }}</h4>
              <div class="text-muted small mb-1">
                <code>{{ clientId }}</code>
              </div>
              <b-badge pill :variant="`light-${statusVariant}`" class="text-capitalize">
                {{ form.status }}
              </b-badge>
              <b-badge pill variant="light-secondary" class="ml-50">
                scope version {{ scopeVersion }}
              </b-badge>
            </b-col>

            <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-50"
                :to="{ name: 'api-clients-usage-view', params: { id: recordId } }"
              >
                <feather-icon icon="ActivityIcon" class="mr-50" />
                Usage
              </b-button>

              <b-button
                v-if="can('api_clients:CanUpdate')"
                variant="outline-danger"
                size="sm"
                @click="onRegenerateSecret"
              >
                <feather-icon icon="RefreshCwIcon" class="mr-50" />
                Regenerate secret
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <api-client-form
        v-model="form"
        :registry="registry"
        :is-edit="true"
        :disabled="loading || !canUpdate"
      />

      <div v-if="canUpdate" class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" :to="{ name: 'api-clients' }">
          Back
        </b-button>
        <b-button variant="primary" :disabled="loading" @click="onSave">
          Save changes
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { get } from 'lodash'
import {
  BRow,
  BCol,
  BCard,
  BBadge,
  BButton,
  BOverlay,
  BCardBody,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import ApiClientForm from '@core/components/api-clients/ApiClientForm.vue'
import ClientSecretModal from '@core/components/api-clients/ClientSecretModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BBadge,
    BButton,
    BOverlay,
    BCardBody,
    ApiClientForm,
    ClientSecretModal,
  },
  data() {
    return {
      loading: false,
      showSecret: false,

      clientId: '',
      scopeVersion: 1,

      registry: { resources: [], client_statuses: [] },

      issued: {
        client_id: '',
        client_secret: '',
      },

      form: {
        title: '',
        description: '',
        contact_name: '',
        contact_email: '',
        status: 'active',
        permissions: [],
        scope_rules: [],
        fixed_fields: [],
        user_provisioning: 'none',
        rate_limit: {
          limit: 3000,
          window_seconds: 60,
        },
        token_ttl_seconds: 3600,
      },
    }
  },
  computed: {
    recordId() {
      return this.$route.params.id
    },
    canUpdate() {
      return this.can('api_clients:CanUpdate')
    },
    statusVariant() {
      if (this.form.status === 'active') return 'success'
      if (this.form.status === 'suspended') return 'warning'
      return 'danger'
    },
  },
  created() {
    this.bootstrap()
  },
  methods: {
    async bootstrap() {
      try {
        this.loading = true

        const [registryRequest, clientRequest] = await Promise.all([
          this.useJwt().mainService.apiClients.registry(),
          this.useJwt().mainService.apiClients.get(this.recordId),
        ])

        this.registry = get(registryRequest, 'data.data', { resources: [] })

        const client = get(clientRequest, 'data.data', {})
        this.clientId = client.client_id || ''
        this.scopeVersion = client.scope_version || 1

        this.form = {
          title: client.title || '',
          description: client.description || '',
          contact_name: client.contact_name || '',
          contact_email: client.contact_email || '',
          status: client.status || 'active',
          permissions: (client.permissions || []).map(permission => ({
            resource: permission.resource,
            actions: [...(permission.actions || [])],
          })),
          scope_rules: (client.scope_rules || []).map(rule => ({
            resource: rule.resource,
            dimension_key: rule.dimension_key,
            mode: rule.mode,
            values: (rule.values || []).map(String),
          })),
          fixed_fields: (client.fixed_fields || []).map(rule => ({
            resource: rule.resource,
            field: rule.field,
            value: rule.value,
          })),
          allowed_ips: (client.allowed_ips || []).join('\n'),
          excluded_fields: (client.excluded_fields || []).map(rule => ({
            resource: rule.resource,
            fields: (rule.fields || []).join(', '),
          })),
          user_provisioning: client.user_provisioning || 'none',
          rate_limit: {
            limit: get(client, 'rate_limit.limit', 3000),
            window_seconds: get(client, 'rate_limit.window_seconds', 60),
          },
          token_ttl_seconds: client.token_ttl_seconds || 3600,
        }
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
    async onSave() {
      try {
        this.loading = true

        await this.useJwt().mainService.apiClients.update(this.recordId, this.form)
        await this.bootstrap()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Client updated successfully',
          },
        })
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
    async onRegenerateSecret() {
      try {
        const result = await this.$swal({
          title: 'Regenerate the client secret?',
          text: 'The current secret stops working immediately and every active token is revoked. The partner must be given the new secret.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, regenerate.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!result.value) return

        this.loading = true

        const request = await this.useJwt().mainService.apiClients.regenerateSecret(this.recordId)
        const issued = get(request, 'data.data', {})

        this.issued = {
          client_id: issued.client_id || this.clientId,
          client_secret: issued.client_secret || '',
        }
        this.showSecret = true
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message,
            },
          })
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
